import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditOutlined from "@mui/icons-material/EditOutlined";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 6,
    padding: "8px 10px",
    backgroundColor: "rgba(0,0,0,0.035)",
    minHeight: 72,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    boxSizing: "border-box",
    width: "100%",
    cursor: "default",
  },
  rootClickable: {
    cursor: "pointer",
  },
  titleRow: {
    width: "100%",
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 4,
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
  },
  toggle: {
    fontSize: 11,
    cursor: "pointer",
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  line1: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  line2: {
    marginTop: 2,
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  muted: {
    opacity: 0.8,
  },
  noPrint: {
    "@media print": { display: "none" },
  },
  editBtn: {
    padding: 4,
    marginLeft: "auto",
    opacity: 0.25,
    "&:hover": { opacity: 0.6 },
  },
  dirty: {
    backgroundColor: "rgba(255,193,7,0.15)",
    boxShadow: "inset 4px 0 0 #f7bd02",
  },
}));

const join = (arr) => arr.filter(Boolean).join(" | ");

const AddressCompact = ({
  title,
  address,
  dirty = false,
  showToggle,
  expanded,
  onToggle,
  onEdit,
  enableClickToEdit,
}) => {
  const classes = useStyles();

  const {
    address1,
    address2,
    address3,
    address4,
    address5,
    city,
    zip,
    country,
    reference,
    email,
    telephone,
    contact,
  } = address || {};

  const mainLine = [
    address1,
    address2,
    address3,
    address4,
    address5,
  ]
    .filter(Boolean)
    .join(", ");

  const locationLine = [city, zip, country].filter(Boolean).join(" ");

  const metaLine = join([
    reference ? `Ref: ${reference}` : null,
    contact ? `Kontakt: ${contact}` : null,
    telephone ? `Tel: ${telephone}` : null,
    email ? `E-post: ${email}` : null,
  ]);

  return (
    <Box
      className={[
        classes.root,
        classes.noPrint,
        dirty ? classes.dirty : null,
        enableClickToEdit && onEdit ? classes.rootClickable : null,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={enableClickToEdit && onEdit ? onEdit : undefined}
    >
      <Box className={classes.titleRow}>
        <Typography className={classes.title}>{title}</Typography>

        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 0.5, }}>
          {showToggle && typeof onToggle === "function" && (
            <Link
              component="button"
              type="button"
              className={classes.toggle}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              underline="none"
            >
              {expanded ? "Dölj full adress" : "Visa full adress"}
            </Link>
          )}

          {onEdit && (
            <Tooltip title="Redigera">
              <IconButton
                size="small"
                className={classes.editBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <EditOutlined fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Typography className={classes.line1} title={mainLine || locationLine}>
        {mainLine || "—"}
        {locationLine ? ` • ${locationLine}` : ""}
      </Typography>

      {metaLine ? (
        <Typography
          className={`${classes.line2} ${classes.muted}`}
          title={metaLine}
        >
          {metaLine}
        </Typography>
      ) : null}
    </Box>
  );
};

AddressCompact.propTypes = {
  title: PropTypes.string.isRequired,
  address: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    address3: PropTypes.string,
    address4: PropTypes.string,
    address5: PropTypes.string,
    city: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string,
    reference: PropTypes.string,
    email: PropTypes.string,
    telephone: PropTypes.string,
    contact: PropTypes.string,
  }),
  dirty: PropTypes.bool,
  showToggle: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,

  // ✅ NYTT
  onEdit: PropTypes.func,
  enableClickToEdit: PropTypes.bool,
};

AddressCompact.defaultProps = {
  address: null,
  showToggle: false,
  expanded: false,
  onToggle: null,

  // ✅ NYTT
  onEdit: null,
  enableClickToEdit: false,
};

export default AddressCompact;
